import { makeAutoObservable, runInAction } from 'mobx'

import { studentController } from '@/controller/student.controller'
import type { GroupWorkbookPage, Student, StudentGroups } from '@/dto'
import { GroupInfo, SchoolGrade } from '@/dto'
import commonRepo from '@/repository/common.repo'
import { RouteName } from '@/router'

/***
 * Lesson 페이지의 URL을 분석하여 Data로 변환하여 가지고 있는 서비스
 ***/
export class LessonGroupService {
  studentGroups?: StudentGroups

  // 각 학생/그룹별 마지막 URL
  groupLastPath = new Map<GroupInfo['key'], string>()
  // 수업탭의 마지막 GroupInfo
  urlGroupInfo?: GroupInfo
  uiProblemScrollTop = new Map<GroupWorkbookPage['pageNum'], number>()

  constructor() {
    makeAutoObservable(this)
  }

  get groupKey() {
    return this.urlGroupInfo?.strKey ?? SchoolGrade.KO_LABEL[0]
  }

  get groupName() {
    return this.urlGroupInfo?.name ?? SchoolGrade.KO_LABEL[0]
  }

  get groupType() {
    return this.urlGroupInfo?.data.type ?? ''
  }

  get groupStudents() {
    if (!this.urlGroupInfo) return

    switch (this.urlGroupInfo.data.type) {
      case 'student': {
        const student = this.studentGroups?.getStudent(this.urlGroupInfo)
        if (!student) return
        return [student]
      }
      case 'grade':
      case 'lesson-class':
        return this.studentGroups?.getGroupFromGroupInfo(this.urlGroupInfo)?.students.toArr
    }
  }

  private _lessonLastPath?: string | null // 수업탭의 마지막 경로

  get lessonLastPath() {
    const { router } = commonRepo
    return (
      this._lessonLastPath ??
      (LessonGroupService.isLessonPage(router?.location.pathname ?? '') &&
        router?.location.pathname)
    )
  }

  // Redirect 초기 경로
  get fallbackPath() {
    if (this.lessonLastPath) return this.lessonLastPath
    const pathData = LessonGroupService.getPathData(commonRepo.router.location.pathname)
    // 이전 URL에 group 정보가 있는 URL이라면 리다이렉트 안함
    if (pathData?.tab || pathData?.groupType || pathData?.key)
      return commonRepo.router.location.pathname

    // 첫번째 학년이 없을 시(재학중인 학생이 아무도 없을 때)
    if (!this.studentGroups?.firstGradeGroup) return RouteName.lesson

    const schoolGrade = SchoolGrade.fromKoLabel(this.studentGroups?.firstGradeGroup.id)
    // 학년 정보 만들기 실패시
    if (!schoolGrade) return RouteName.lesson

    const groupInfo = new GroupInfo({
      type: 'grade',
      schoolGrade,
    })

    return `${RouteName.lessonWorksheet}/${groupInfo.data.type}/${groupInfo.key}`
  }

  // 수업 URL 생성
  static makeGroupInfoPathName = ({
    groupInfo,
    baseUrl,
  }: {
    groupInfo?: GroupInfo
    baseUrl?: string
  }) => {
    const pathname = commonRepo.router.getLocationInfo().fullPath

    const newPathname = pathname.replace(
      /\/([^\\/]+)\/([^\\/]+)\/([^\\/]+)\/([^\\/]+)/,
      (full, $1, $2, groupType, groupKey) => {
        return `${baseUrl ? baseUrl : `/${$1}/${$2}`}/${groupInfo?.data.type ?? groupType}/${
          groupInfo?.key ?? groupKey
        }`
      },
    )

    return newPathname
  }

  // lesson 페이지인지
  static isLessonPage(pathname: string) {
    return !!pathname?.includes('/lesson/')
  }

  static getPathData(pathname: string) {
    if (LessonGroupService.isLessonPage(pathname)) {
      const match = pathname.match(/\/[^\\/]+\/([^\\/]+)\/([^\\/]+)\/([^\\/]+)/)
      if (match) {
        const [, tab, groupType, key] = match

        return {
          tab,
          groupType,
          key,
        }
      }
    }
  }

  // URL을 통해서 GroupInfo를 얻음
  static async getUrlGroupInfoFromPath(
    path: string,
    _pathData?: ReturnType<typeof LessonGroupService.getPathData>,
  ) {
    const studentGroups = await studentController.fetchStudentGroups({ serviceType: 'SCIENCEFLAT' })
    const pathData = _pathData ? _pathData : LessonGroupService.getPathData(path)
    if (!pathData?.tab || !pathData?.groupType || !pathData?.key) return

    const groupInfo = (() => {
      switch (pathData?.groupType) {
        case 'grade': {
          const schoolGrade = SchoolGrade.fromKoLabel(
            pathData.key as (typeof SchoolGrade.KO_LABEL)[number],
          )

          if (schoolGrade) {
            return new GroupInfo({
              type: 'grade',
              schoolGrade,
            })
          }
          break
        }
        case 'lesson-class': {
          const studentLessonClass = studentGroups?.studentsByLessonClass?.get(Number(pathData.key))

          if (studentLessonClass) {
            return new GroupInfo({
              type: 'lesson-class',
              classId: studentLessonClass.id,
              name: studentLessonClass.name ?? '',
            })
          }
          break
        }
        case 'student': {
          const student = studentGroups?.getStudent(pathData.key as Student['id'])
          if (student) {
            return new GroupInfo({
              type: 'student',
              studentId: student.id,
              name: student.name,
            })
          }
          break
        }
      }
    })()

    return groupInfo
  }

  setGroupInfo(groupInfo: GroupInfo) {
    this.urlGroupInfo = groupInfo
  }

  async fetchStudentGroups() {
    const studentGroups = await studentController.fetchStudentGroups({ serviceType: 'SCIENCEFLAT' })
    runInAction(() => (this.studentGroups = studentGroups))
  }

  setLessonLastPath(fullPath: string) {
    if (!LessonGroupService.isLessonPage(fullPath)) return
    if (this._lessonLastPath === fullPath) return

    this._lessonLastPath = fullPath
  }
}
